import { c as _c } from "react/compiler-runtime";
import { motion, useMotionValue, useMotionValueEvent, useScroll, useTransform } from 'framer-motion';
import React, { PropsWithChildren, useRef } from 'react';
import { useClientOnly } from '@/hooks/useClientOnly';
import { SectionDelimiter, DEFAULT_RATIO } from '@/components/SectionDelimiter';
import { resolveAnchorId } from '@/utils/anchors';
import { mergeClasses } from '@/utils/style';
type ColorVariant = 'bg-blue-900' | 'bg-blue-700' | 'bg-blue-500' | 'bg-blue-100' | 'bg-blue-50' | 'bg-transparent' | 'bg-white';
interface AnimatedSectionProps {
  className?: string;
  bgColor: ColorVariant;
}
type AnimatedSectionAnchorProps = {
  anchorKey: string;
  id?: string;
} | {
  id: string;
  anchorKey?: string;
};
export const AnimatedSection = t0 => {
  const $ = _c(39);
  const {
    className,
    children,
    bgColor,
    anchorKey,
    id
  } = t0;
  const ref = useRef(null);
  const {
    isClient
  } = useClientOnly();
  const scrollY = useMotionValue(0);
  const t1 = isClient ? ref : undefined;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["400px end", "start start"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== t1) {
    t3 = {
      target: t1,
      offset: t2
    };
    $[1] = t1;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    scrollYProgress
  } = useScroll(t3);
  let t4;
  if ($[3] !== scrollY) {
    t4 = value => scrollY.set(value);
    $[3] = scrollY;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useMotionValueEvent(scrollYProgress, "change", t4);
  const ratio = useTransform(scrollY, _temp);
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [0, 1];
    t6 = [0, -300];
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  const y = useTransform(scrollYProgress, t5, t6);
  let t7;
  if ($[7] !== anchorKey || $[8] !== id) {
    t7 = resolveAnchorId(anchorKey, id);
    $[7] = anchorKey;
    $[8] = id;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const resolvedId = t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      y: 0
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== y) {
    t9 = {
      y
    };
    $[11] = y;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== bgColor || $[14] !== ratio) {
    t10 = <SectionDelimiter color={bgColor} background="bg-transparent" ratio={ratio} />;
    $[13] = bgColor;
    $[14] = ratio;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== bgColor) {
    t11 = mergeClasses("absolute top-full right-0 left-0 -mt-[1px] h-[50rem]", bgColor);
    $[16] = bgColor;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11) {
    t12 = <div className={t11} />;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t12 || $[22] !== t9) {
    t13 = <motion.div initial={t8} style={t9} className="pointer-events-none will-change-transform">{t10}{t12}</motion.div>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t9;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== bgColor || $[25] !== className) {
    t14 = mergeClasses("relative z-10 pt-10 pb-20 lg:pt-20 lg:pb-40", bgColor, className);
    $[24] = bgColor;
    $[25] = className;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== children || $[28] !== t14) {
    t15 = <div className={t14}>{children}</div>;
    $[27] = children;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== bgColor) {
    t16 = mergeClasses("absolute top-full right-0 left-0 h-[10rem]", bgColor);
    $[30] = bgColor;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t16) {
    t17 = <div className={t16} />;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== resolvedId || $[35] !== t13 || $[36] !== t15 || $[37] !== t17) {
    t18 = <section ref={ref} id={resolvedId} className="relative z-30">{t13}{t15}{t17}</section>;
    $[34] = resolvedId;
    $[35] = t13;
    $[36] = t15;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  return t18;
};
function _temp(value_0) {
  return (1 - value_0) * DEFAULT_RATIO;
}